// @flow

// Types
import type {
  FertilizerOperation,
} from '../../types'

import type {
  Fertilizer,
} from 'fc-model-fertilizers/types'

// Libs
import _every from 'lodash/every'
import _map from 'lodash/map'
import _size from 'lodash/size'

// Operator
import operator from '../operator'
import validateOperation from './validateOperation'

// Controller
import controller from 'fc-ctrl-fertilizers'

// Effect Managers
import { call, fork, put, race, select, take } from 'redux-saga/effects'

type OperationsArgs = {
  operations$: (state: Object) => Array<FertilizerOperation>
}

// Exports
export default function *watchOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  while (true) {
    yield take([operator.actionTypes.add(), operator.actionTypes.edit(), operator.actionTypes.delete()])
    yield fork(handleOperations, {operations$})
  }
}

export function *handleOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  let done = false

  while (!done) {
    const effect = yield race({
      submit: take(operator.actionTypes.submitOperations()),
      cancel: take(operator.actionTypes.cancelOperations()),
    })

    if (effect.submit) {
      done = yield call(handleSubmitOperations, {operations$})
    } else {
      done = yield call(handleCancelOperations, {operations$})
    }
  }
}

export function *handleCancelOperations ({operations$}: OperationsArgs): Generator<*, boolean, *> {
  const operations = yield select(operations$)
  const action = yield operator.actionCreators.cancelOperations(operations)
  yield put(action)
  return true
}

export function *handleSubmitOperations ({operations$}: OperationsArgs): Generator<*, boolean, *> {
  const operations = yield select(operations$)

  const nextOperations: Array<FertilizerOperation> =
  yield _map(operations, function (operation: FertilizerOperation): Object {
      return call(handleSubmitOperation, operation)
    })

  return _every(nextOperations, function (operation: FertilizerOperation): boolean {
    return operation.success
  })
}

export function *handleSubmitOperation (operation: FertilizerOperation): Generator<*, FertilizerOperation, *> {
  const validatedOperation = yield call(validateOperation, operation)
  if (_size(validatedOperation.errors)) {
    return validatedOperation
  }

  const model: Fertilizer = operation.model

  const proc = operation.operation === 'ADD'
    ? controller.create
    : operation.operation === 'EDIT'
      ? controller.update
      : controller.delete
  const { error, response } = yield call([controller, proc], {
    model,
  })

  if (error) {
    const nextOperation = {
      ...operation,
      submitting: false,
      success: false,
      errors: response,
    }

    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)

    return nextOperation
  } else {
    const nextOperation = {
      ...operation,
      submitting: false,
      success: true,
    }

    const action = operator.actionCreators.cancelOperation(nextOperation)
    yield put(action)

    return nextOperation
  }
}
